import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { InterfaceArea } from 'app/models/area.model';
import { InterfaceDepartment } from 'app/models/department.model';
import { DepartmentService } from 'app/services/department.service';
import { SweetalertService } from 'app/services/sweetalert.service';
import { timezone } from 'assets/constants/timezone';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AddAreaComponent } from '../add-area/add-area.component';
import { AddIndicatorComponent } from '../add-indicator/add-indicator.component';
import { CreateDepartmentComponent } from '../create-department/create-department.component';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezoneDayJs from 'dayjs/plugin/timezone';
import { IndicatorService } from 'app/services/indicator.service';
dayjs.extend(utc);
dayjs.extend(timezoneDayJs);

@Component({
  selector: 'app-departments-table',
  templateUrl: './departments-table.component.html',
  styleUrls: ['./departments-table.component.scss']
})
export class DepartmentsTableComponent implements OnInit, OnDestroy {

  departments: InterfaceDepartment[] = [];
  subscriptions: Subscription[] = [];
  selectedDepartment: InterfaceDepartment = {};
  selectedIndex = -1;
  showDepartments = true;
  areas: InterfaceArea[] = [];
  indicators: any = {};
  @Input() infoIndicator: any;
  currentMonth: number;

  constructor(
    protected departmentService: DepartmentService,
    protected indicatorService: IndicatorService,
    protected swal: SweetalertService,
    public modal: NgbModal
  ) {
    this.currentMonth = Number(dayjs().tz(timezone).format('MM'));
   }

  ngOnInit(): void {
    this.getDepartments();
  }

  getDepartments(): void {
    this.subscriptions.push(
      this.departmentService.getDepartments().pipe(take(1)).subscribe((response: any) => {
        this.departments = this.departments.concat(response);
      }, (error: any) => {
        console.error(error);
      })
    );
  }

  resetDepartments(): void {
    this.departments = [];
    this.getDepartments();
  }

  scrollDepartments(): void {
    this.subscriptions.push(
      this.departmentService.getDepartments(this.departments[this.departments.length - 1]).pipe(take(1)).subscribe((response: any) => {
        this.departments = this.departments.concat(response);
      }, (error: any) => {
        console.error(error);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => {
      sub.unsubscribe();
    });
  }

  selectDepartment(department: InterfaceDepartment, index: number): void {
    this.selectedDepartment = department;
    this.selectedIndex = index;
    this.showDepartments = false;
    this.getAreas();
  }

  resetAreas(): void {
    this.areas = [];
    this.getAreas();
  }

  getAreas(): void {
    this.subscriptions.push(
      this.departmentService.getAreas(this.selectedDepartment.id!).pipe(take(1)).subscribe((result: any) => {
        this.areas = result;
        for (let i = 0; i < this.areas.length; i++) {
          this.getIndicators(this.areas[i].id!);
        }
      }, (error: any) => {
        console.error(error);
      })
    );
  }

  resetIndicators(areaId: string): void {
    this.indicators[areaId] = [];
    this.getIndicators(areaId);
  }

  getIndicators(areaId: string): void {
    this.subscriptions.push(
      this.departmentService.getIndicators(areaId).pipe(take(1)).subscribe((result: any) => {
        this.indicators[areaId] = result;
      }, (error: any) => {
        console.error(error);
      })
    );
  }

  backToDepartments(): void {
    this.selectedDepartment = {};
    this.selectedIndex = -1;
    this.showDepartments = true;
  }

  addDepartment(): void {
    const modalRef = this.modal.open(CreateDepartmentComponent, {
      centered: true,
      backdrop: "static",
      animation: true
    });
    modalRef.result.then((result: any) => {
      this.resetDepartments();
      if (result && result.success) {
        this.swal.showSuccess();
      } else if (result && !result.success) {
        this.swal.showError();
      } else {
        this.swal.showCancel();
      }
    }).catch((error: any) => {
      console.error(error);
      this.swal.showError();
    });
  }

  addArea(department: InterfaceDepartment): void {
    const modalRef = this.modal.open(AddAreaComponent, {
      centered: true,
      backdrop: "static",
      animation: true
    });
    modalRef.componentInstance.department = department;
    modalRef.result.then((result: any) => {
      if (result && result.success) {
        this.swal.showSuccess();
      } else if (result && !result.success) {
        this.swal.showError();
      } else {
        this.swal.showCancel();
      }
      this.resetAreas();
    }).catch((error: any) => {
      console.error(error);
      this.swal.showError();
    });
  }

  addIndicator(area: InterfaceArea): void {
    const modalRef = this.modal.open(AddIndicatorComponent, {
      centered: true,
      backdrop: "static",
      animation: true
    });
    modalRef.componentInstance.data = {
      area: area,
      department: this.selectedDepartment
    };
    modalRef.result.then((result: any) => {
      if (result && result.success) {
        this.swal.showSuccess();
      } else if (result && !result.success) {
        this.swal.showError();
      } else {
        this.swal.showCancel();
      }
      this.resetIndicators(area.id!);
    }).catch((error: any) => {
      console.error(error);
      this.swal.showError();
    });
  }

  getDateFormat(unixDate: number): string {
    return dayjs.unix(unixDate).tz(timezone).format("DD/MM/YYYY");
  }

  returnChangedFormat(indicatorType: number, amount: number): string {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    switch (indicatorType) {
      case 1:
        if (amount) {
          return formatter.format(amount);
        } else {
          return formatter.format(0);
        }
      case 2:
        if (amount) {
          return amount + "%"
        } else {
          return "0%"
        }
      case 3:
        if (amount) {
          return amount.toString();
        } else {
          return "0";
        }
      default:
        return "";
    }
  }
  
  returnMonthFormat(month: number): string {
    switch(month){
      case 1:
        return 'Enero'
      case 2:
        return 'Febrero'
      case 3:
        return 'Marzo'
      case 4:
        return 'Abril'
      case 5:
        return 'Mayo'
      case 6:
        return 'Junio'
      case 7:
        return 'Julio'
      case 8:
        return 'Agosto'
      case 9:
        return 'Septiembre'
      case 10:
        return 'Octubre'
      case 11:
        return 'Noviembre'
      case 12:
        return 'Diciembre'
    }
  }

  updateIndicator(data: any): void{
    this.departmentService.getIndicators(data.id).pipe(take(1)).subscribe((result: any) => {
      const modalRef = this.modal.open(AddIndicatorComponent, {
        centered: true,
        backdrop: "static",
        animation: true
      });
      modalRef.componentInstance.data = {
        area: data,
        department: this.selectedDepartment,
        isUpdate: true
      };
      modalRef.result.then((result: any) => {
        if (result && result.success) {
          this.resetIndicators(data.areaId!);
          //this.swal.showSuccess();
        } else if (result && !result.success) {
          this.swal.showCancel();
        } else {
          //this.swal.showCancel();
        }
      }).catch((error: any) => {
        console.error(error);
        this.swal.showError();
      });
    }, (error: any) => {
      console.error(error);
    });
  }
}
